import { Controller } from '@hotwired/stimulus'

export default class PasswordStrengthController extends Controller {
  static targets = [
    'suggestionContainer',
    'suggestionText',
    'passwordInput',
    'passwordConfirmationInput',
    'lengthConditionIcon',
    'upperLowerConditionIcon',
    'digitConditionIcon',
    'specialCharacterConditionIcon',
    'passwordMatchConditionIcon',
    'submitButton',
  ];

  static values = {
    checkStrengthUrl: String
  };

  connect() {
    this.conditionNeutralClass = 'text-gray-200';
    this.conditionSuccessClass = 'text-success';
    this.checkStrengthDebounceMs = 300;
    this.checkStrengthTimeoutId = null;
    this.conditionsTotalCount = [
      this.hasLengthConditionIconTarget,
      this.hasUpperLowerConditionIconTarget,
      this.hasDigitConditionIconTarget,
      this.hasSpecialCharacterConditionIconTarget,
      this.hasPasswordMatchConditionIconTarget,
    ].filter(Boolean).length;
  }

  check(event) {
    const password = this.passwordInputTarget.value;
    this.updateConditionIcons(password);
    this.enableDisableContinueButton();
    if (event.target === this.passwordInputTarget) {
      this.scheduleCheckStrength(password);
    }
  }

  scheduleCheckStrength(password) {
    if (this.checkStrengthTimeoutId) {
      clearTimeout(this.checkStrengthTimeoutId);
    }

    this.checkStrengthTimeoutId = setTimeout(() => {
      this.checkStrengthTimeoutId = null;
      this.checkStrength(password);
    }, this.checkStrengthDebounceMs);
  }

  async checkStrength(password) {
    let warning = null;

    if (password.length > 0) {
      try {
        const response = await fetch(this.checkStrengthUrlValue, {
          method: 'POST',
          body: JSON.stringify({password}),
          headers: {
            'X-CSRF-Token': document.querySelector('meta[name=\'csrf-token\']').content,
            'Content-Type': 'application/json',
          },
        });
        const result = await response.json();
        warning = result.warning;
      } catch (error) {
      }
    }

    this.updateStrengthFeedback(warning);
  }

  updateStrengthFeedback(warning) {
    if (warning && warning.length > 0) {
      this.suggestionTextTarget.innerText = warning;
      this.suggestionContainerTarget.classList.remove('d-none');
    } else {
      this.suggestionContainerTarget.classList.add('d-none');
    }
  }

  enableDisableContinueButton() {
    if (this.conditionsValidCount < this.conditionsTotalCount) {
      this.submitButtonTarget.setAttribute('disabled', true);
    } else {
      this.submitButtonTarget.removeAttribute('disabled');
    }
  }

  updateConditionIcons(password) {
    this.conditionsValidCount = 0;
    this.updateConditionIcon(this.lengthConditionIconTarget, this.hasMinLength(password));
    this.updateConditionIcon(this.upperLowerConditionIconTarget, this.hasUpperAndLower(password));
    this.updateConditionIcon(this.digitConditionIconTarget, this.hasDigit(password));
    this.updateConditionIcon(this.specialCharacterConditionIconTarget, this.hasSpecialCharacter(password));
    if (this.hasPasswordMatchConditionIconTarget && this.hasPasswordConfirmationInputTarget) {
      this.updateConditionIcon(this.passwordMatchConditionIconTarget, password.length > 0 && password === this.passwordConfirmationInputTarget.value);
    }
  }

  updateConditionIcon(iconTarget, isValid) {
    iconTarget.classList.remove(this.conditionSuccessClass, this.conditionNeutralClass);
    if (isValid) {
      this.conditionsValidCount++;
      iconTarget.classList.add(this.conditionSuccessClass);
    } else {
      iconTarget.classList.add(this.conditionNeutralClass);
    }
  }

  hasMinLength(password, minLength = 10) {
    return password.length >= minLength;
  }

  hasUpperAndLower(password) {
    return /[A-Z]/.test(password) && /[a-z]/.test(password);
  }

  hasDigit(password) {
    return /[0-9]/.test(password);
  }

  hasSpecialCharacter(password) {
    return /[^A-Za-z0-9]/.test(password);
  }
}
